/**
 * Meeting Detail Page - Refactored Version
 * 
 * Uses modular components from @/components/MeetingIntelligence/
 * Reduced from 2600+ lines to ~800 lines
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, ArrowLeft, Video, Building2, Users, Clock,
  MapPin, FileText, Mic, PlayCircle, StopCircle, 
  Upload, Wand2, CheckCircle, Send, Edit, Save,
  AlertCircle, Loader2, RefreshCw, ThumbsUp, ThumbsDown,
  ListChecks, GitBranch, Mail, Phone, Globe, MessageCircle
} from 'lucide-react';

// Import modular tab components
import {
  OverviewTab,
  TasksTab,
  DecisionsTab,
  MeetingHeader,
  RelatedMeetings,
  TranscriptTab,
  AnalysisTab,
  MinutesTab,
  RecordingTab
} from '@/components/MeetingIntelligence';

const API = process.env.REACT_APP_BACKEND_URL;

const MeetingDetail = () => {
  const { meetingId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoRecord = searchParams.get('autoRecord') === 'true';
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [meeting, setMeeting] = useState(null);
  const [recording, setRecording] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [minutes, setMinutes] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [decisions, setDecisions] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [recordingFileMissing, setRecordingFileMissing] = useState(false);
  
  // Processing state
  const [processing, setProcessing] = useState({
    transcription: false,
    analysis: false,
    mom: false,
    sending: false,
    diarization: false
  });
  const [transcriptionProgress, setTranscriptionProgress] = useState(0);
  
  // Dialog state
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [sendDialog, setSendDialog] = useState(false);
  const [externalUrlDialog, setExternalUrlDialog] = useState(false);
  const [reminderDialog, setReminderDialog] = useState(false);
  const [showPostRecordingForm, setShowPostRecordingForm] = useState(false);
  
  // Form state
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [editingMinutes, setEditingMinutes] = useState(false);
  const [minutesText, setMinutesText] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [sendMethod, setSendMethod] = useState('email');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [whatsappNumbers, setWhatsappNumbers] = useState('');
  const [reminderType, setReminderType] = useState('upcoming');
  const [customMessage, setCustomMessage] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  
  // Post-recording form state
  const [postRecordingData, setPostRecordingData] = useState({
    title: '', description: '', attendees: [], location: ''
  });
  const [orgUsers, setOrgUsers] = useState([]);
  const [externalAttendeeInput, setExternalAttendeeInput] = useState({ name: '', email: '' });
  const [savingMeetingDetails, setSavingMeetingDetails] = useState(false);
  const [recordingStartTime, setRecordingStartTime] = useState(null);
  const [recordingEndTime, setRecordingEndTime] = useState(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  
  // Audio Settings State
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('default');
  const [audioPreset, setAudioPreset] = useState('high_quality');
  const [showAudioSettings, setShowAudioSettings] = useState(false);
  
  // Audio Quality Presets
  const AUDIO_PRESETS = {
    standard: {
      name: 'Standard',
      description: 'Good for 1-on-1 calls',
      constraints: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 44100, channelCount: 1 },
      bitrate: 128000
    },
    high_quality: {
      name: 'High Quality',
      description: 'Best for small meetings',
      constraints: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, sampleRate: 48000, channelCount: 1 },
      bitrate: 192000
    },
    conference: {
      name: 'Conference Room',
      description: 'Optimized for large rooms',
      constraints: { echoCancellation: false, noiseSuppression: true, autoGainControl: false, sampleRate: 48000, channelCount: 2 },
      bitrate: 256000
    }
  };
  
  // Refs
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const speechRecognitionRef = useRef(null);

  // Fetch meeting data
  const fetchMeetingData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/meeting-intelligence/meetings/${meetingId}?token=${token}`);
      
      setMeeting(res.data.meeting);
      setRecording(res.data.recording);
      setTranscript(res.data.transcript);
      setMinutes(res.data.minutes);
      setTasks(res.data.tasks || []);
      setDecisions(res.data.decisions || []);
      setAnalysis(res.data.analysis || null);
      
      if (res.data.transcript) {
        setTranscriptText(res.data.transcript.text);
      }
      if (res.data.minutes) {
        setMinutesText(res.data.minutes.content);
      }
    } catch (err) {
      console.error('Error fetching meeting:', err);
      toast.error('Failed to load meeting details');
    } finally {
      setLoading(false);
    }
  };

  // Fetch org users
  const fetchOrgUsers = async () => {
    try {
      const res = await axios.get(`${API}/api/meeting-intelligence/users?token=${token}`);
      setOrgUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching org users:', err);
    }
  };

  useEffect(() => {
    fetchMeetingData();
    fetchOrgUsers();
    loadAudioDevices();
    
    // Listen for device changes
    navigator.mediaDevices?.addEventListener('devicechange', loadAudioDevices);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', loadAudioDevices);
    };
  }, [meetingId, token]);

  // Load available audio input devices
  const loadAudioDevices = async () => {
    try {
      // Request permission first to get device labels
      await navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => stream.getTracks().forEach(track => track.stop()));
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(d => d.kind === 'audioinput');
      setAudioDevices(audioInputs);
    } catch (err) {
      console.error('Failed to load audio devices:', err);
    }
  };

  // Auto-start recording for quick start meetings
  useEffect(() => {
    if (autoRecord && meeting && !loading && !isRecording && !recording) {
      const timer = setTimeout(() => startRecording(), 1000);
      return () => clearTimeout(timer);
    }
  }, [autoRecord, meeting, loading]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (audioContextRef.current) audioContextRef.current.close().catch(() => {});
      if (speechRecognitionRef.current) {
        try { speechRecognitionRef.current.stop(); } catch(e) {}
      }
    };
  }, []);

  // ============ Recording Functions ============
  const initSpeechRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return null;
    
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onresult = (event) => {
      let interim = '', final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const text = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += text + ' ';
        else interim += text;
      }
      if (final) setLiveTranscript(prev => prev + final);
      setInterimTranscript(interim);
    };
    
    recognition.onerror = () => {};
    recognition.onend = () => {
      if (mediaRecorder?.state === 'recording') {
        setTimeout(() => { try { recognition.start(); } catch(e) {} }, 100);
      }
    };
    
    return recognition;
  };

  const startRecording = async () => {
    try {
      const preset = AUDIO_PRESETS[audioPreset];
      
      // Build audio constraints with selected device
      const audioConstraints = {
        ...preset.constraints,
        deviceId: selectedDevice !== 'default' ? { exact: selectedDevice } : undefined
      };
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });
      
      // Log device info
      const audioTrack = stream.getAudioTracks()[0];
      console.log('Recording with:', audioTrack.label, 'Preset:', preset.name);
      
      // Setup audio monitoring
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: preset.constraints.sampleRate });
      const analyser = audioContext.createAnalyser();
      audioContext.createMediaStreamSource(stream).connect(analyser);
      analyser.fftSize = 512;
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      
      const checkLevel = () => {
        if (analyserRef.current) {
          const data = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(data);
          setAudioLevel(Math.min(100, data.slice(4, 130).reduce((a,b) => a+b) / 126 * 1.5));
        }
        animationFrameRef.current = requestAnimationFrame(checkLevel);
      };
      checkLevel();
      
      // Setup recorder with preset bitrate
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: preset.bitrate });
      const chunks = [];
      
      recorder.ondataavailable = (e) => chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        if (audioContextRef.current) audioContextRef.current.close();
        setAudioLevel(0);
        
        const blob = new Blob(chunks, { type: mimeType });
        await uploadRecording(blob);
      };
      
      recorder.start(1000);
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      setRecordingStartTime(new Date());
      setLiveTranscript('');
      
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
      
      const recognition = initSpeechRecognition();
      if (recognition) {
        speechRecognitionRef.current = recognition;
        try { recognition.start(); } catch(e) {}
      }
      
      if (meeting.status === 'scheduled') {
        await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/start?token=${token}`);
      }
      
      toast.success('Recording started');
    } catch (err) {
      console.error('Error starting recording:', err);
      toast.error('Failed to access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      setRecordingEndTime(new Date());
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(t => t.stop());
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) clearInterval(timerRef.current);
      if (speechRecognitionRef.current) speechRecognitionRef.current.stop();
      
      if (meeting?.is_quick_start) setShowPostRecordingForm(true);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder?.state === 'recording') {
      mediaRecorder.pause();
      setIsPaused(true);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      setAudioLevel(0);
      toast.info('Recording paused');
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder?.state === 'paused') {
      mediaRecorder.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
      
      const checkLevel = () => {
        if (analyserRef.current && mediaRecorder?.state === 'recording') {
          const data = new Uint8Array(analyserRef.current.frequencyBinCount);
          analyserRef.current.getByteFrequencyData(data);
          setAudioLevel(Math.min(100, data.reduce((a,b) => a+b) / data.length * 1.5));
          animationFrameRef.current = requestAnimationFrame(checkLevel);
        }
      };
      checkLevel();
      toast.success('Recording resumed');
    }
  };

  const uploadRecording = async (blob) => {
    const formData = new FormData();
    formData.append('file', blob, `recording_${Date.now()}.webm`);
    
    try {
      setUploadProgress(0);
      toast.info('Uploading recording...');
      
      await axios.post(
        `${API}/api/meeting-intelligence/meetings/${meetingId}/recording/upload?token=${token}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total))
        }
      );
      
      setUploadProgress(100);
      toast.success('Recording uploaded! Starting transcription...');
      setRecordingFileMissing(false);
      
      if (meeting.status === 'in_progress') {
        await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/end?token=${token}`).catch(() => {});
      }
      
      await fetchMeetingData();
      setTimeout(() => runTranscription(), 1000);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadProgress(0);
      toast.error(err.response?.data?.detail || 'Failed to upload recording');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      setUploadProgress(0);
      toast.info(`Uploading ${file.name}...`);
      
      await axios.post(
        `${API}/api/meeting-intelligence/meetings/${meetingId}/recording/upload?token=${token}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 600000,
          onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total))
        }
      );
      
      setUploadProgress(100);
      toast.success('File uploaded! Starting transcription...');
      await fetchMeetingData();
      setTimeout(() => runTranscription(), 1000);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadProgress(0);
      toast.error(err.response?.data?.detail || 'Failed to upload file');
    }
  };

  // ============ AI Processing Functions ============
  const runTranscription = async () => {
    try {
      setProcessing(p => ({ ...p, transcription: true }));
      setTranscriptionProgress(0);
      
      const progressInterval = setInterval(() => {
        setTranscriptionProgress(p => Math.min(95, p + 2));
      }, 1000);
      
      await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/transcribe?token=${token}`, null, { timeout: 600000 });
      
      clearInterval(progressInterval);
      setTranscriptionProgress(100);
      toast.success('Transcription completed!');
      fetchMeetingData();
    } catch (err) {
      console.error('Transcription failed:', err);
      const detail = err.response?.data?.detail || 'Transcription failed';
      if (detail.includes('file not found')) {
        setRecordingFileMissing(true);
        toast.error('Recording file not found. Please re-upload.');
      } else {
        toast.error(detail);
      }
    } finally {
      setProcessing(p => ({ ...p, transcription: false }));
      setTranscriptionProgress(0);
    }
  };

  const runAnalysis = async () => {
    try {
      setProcessing(p => ({ ...p, analysis: true }));
      const res = await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/analyze?token=${token}`);
      toast.success(`Analysis complete: ${res.data.tasks_created} tasks, ${res.data.decisions_created} decisions`);
      fetchMeetingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Analysis failed');
    } finally {
      setProcessing(p => ({ ...p, analysis: false }));
    }
  };

  const generateMoM = async () => {
    try {
      setProcessing(p => ({ ...p, mom: true }));
      await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/minutes/generate?token=${token}`);
      toast.success('Minutes generated!');
      fetchMeetingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'MoM generation failed');
    } finally {
      setProcessing(p => ({ ...p, mom: false }));
    }
  };

  const runDiarization = async () => {
    try {
      setProcessing(p => ({ ...p, diarization: true }));
      const res = await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/identify-speakers?token=${token}`);
      toast.success(`Identified ${res.data.speakers_identified} speakers!`);
      fetchMeetingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speaker identification failed');
    } finally {
      setProcessing(p => ({ ...p, diarization: false }));
    }
  };

  // ============ Save Functions ============
  const saveTranscript = async () => {
    try {
      await axios.put(`${API}/api/meeting-intelligence/meetings/${meetingId}/transcript?token=${token}`, { text: transcriptText });
      toast.success('Transcript saved');
      setEditingTranscript(false);
      fetchMeetingData();
    } catch (err) {
      toast.error('Failed to save transcript');
    }
  };

  const saveMinutes = async () => {
    try {
      await axios.put(`${API}/api/meeting-intelligence/meetings/${meetingId}/minutes?token=${token}`, { content: minutesText });
      toast.success('Minutes saved');
      setEditingMinutes(false);
      fetchMeetingData();
    } catch (err) {
      toast.error('Failed to save minutes');
    }
  };

  const approveMoM = async (action) => {
    try {
      await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/minutes/approve?token=${token}`, { action, comments: '' });
      toast.success(`Minutes ${action === 'approve' ? 'approved' : 'rejected'}`);
      setApprovalDialog(false);
      fetchMeetingData();
    } catch (err) {
      toast.error('Failed to process approval');
    }
  };

  const sendMoM = async () => {
    try {
      setProcessing(p => ({ ...p, sending: true }));
      
      if (sendMethod === 'email') {
        const recipients = selectedRecipients.length > 0 ? selectedRecipients : meeting.attendees?.map(a => a.email).filter(Boolean) || [];
        if (recipients.length === 0) { toast.error('No recipients'); return; }
        await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/minutes/send?token=${token}`, { recipients });
        toast.success('Minutes sent via email');
      } else {
        const numbers = whatsappNumbers.split(',').map(n => n.trim()).filter(Boolean);
        if (numbers.length === 0) { toast.error('Enter WhatsApp numbers'); return; }
        await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/minutes/send-whatsapp?token=${token}`, { phone_numbers: numbers });
        toast.success('Minutes sent via WhatsApp');
      }
      
      setSendDialog(false);
      fetchMeetingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setProcessing(p => ({ ...p, sending: false }));
    }
  };

  const saveExternalUrl = async () => {
    if (!externalUrl.trim()) { toast.error('Enter a valid URL'); return; }
    try {
      await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/recording/url?token=${token}`, { recording_url: externalUrl, recording_name: 'External Recording' });
      toast.success('Recording URL saved');
      setExternalUrlDialog(false);
      setExternalUrl('');
      fetchMeetingData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save URL');
    }
  };

  const sendReminder = async () => {
    try {
      setSendingReminder(true);
      await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/send-reminder?token=${token}`, { reminder_type: reminderType, custom_message: customMessage || null });
      toast.success('Reminder sent');
      setReminderDialog(false);
      setCustomMessage('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const endMeeting = async () => {
    try {
      await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/end?token=${token}`);
      toast.success('Meeting ended');
      fetchMeetingData();
    } catch (err) {
      toast.error('Failed to end meeting');
    }
  };

  const sendWhatsAppNotification = async () => {
    try {
      setSendingWhatsApp(true);
      await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/notify-whatsapp?token=${token}`);
      toast.success('Summary sent to WhatsApp group!');
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send WhatsApp notification';
      toast.error(errorMsg);
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handlePostRecordingSubmit = async () => {
    try {
      setSavingMeetingDetails(true);
      const payload = {
        title: postRecordingData.title || `Meeting - ${new Date().toLocaleDateString()}`,
        description: postRecordingData.description,
        attendee_ids: postRecordingData.attendees.filter(a => a.is_internal).map(a => a.user_id),
        external_attendees: postRecordingData.attendees.filter(a => !a.is_internal).map(a => ({ name: a.name, email: a.email })),
        start_time: recordingStartTime?.toISOString(),
        end_time: recordingEndTime?.toISOString(),
        location: postRecordingData.location
      };
      
      await axios.post(`${API}/api/meeting-intelligence/meetings/${meetingId}/quick-start-complete?token=${token}`, payload);
      toast.success('Meeting details saved!');
      setShowPostRecordingForm(false);
      await fetchMeetingData();
      setTimeout(() => generateMoM(), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save details');
    } finally {
      setSavingMeetingDetails(false);
    }
  };

  // Helper functions
  const formatTime = (s) => `${Math.floor(s/60).toString().padStart(2,'0')}:${(s%60).toString().padStart(2,'0')}`;
  const toggleRecipient = (email) => setSelectedRecipients(p => p.includes(email) ? p.filter(e => e !== email) : [...p, email]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Meeting not found</p>
        <Button className="mt-4" onClick={() => navigate('/meeting-intelligence/meetings')}>Back to Meetings</Button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="meeting-detail">
      {/* Header */}
      <MeetingHeader
        meeting={meeting}
        onBack={() => navigate('/meeting-intelligence/meetings')}
        onEndMeeting={endMeeting}
        onSendReminder={() => setReminderDialog(true)}
        onRefresh={fetchMeetingData}
      />

      {/* Main Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="recording">Recording</TabsTrigger>
          <TabsTrigger value="transcript">Transcript</TabsTrigger>
          <TabsTrigger value="analysis">AI Analysis</TabsTrigger>
          <TabsTrigger value="minutes">Minutes</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({tasks.length})</TabsTrigger>
          <TabsTrigger value="decisions">Decisions ({decisions.length})</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <OverviewTab meeting={meeting} recording={recording} tasks={tasks} minutes={minutes} />
            </div>
            <div>
              <RelatedMeetings meetingId={meetingId} token={token} />
            </div>
          </div>
        </TabsContent>

        {/* Recording Tab - Inline for complex state management */}
        <TabsContent value="recording">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mic className="h-5 w-5 text-red-500" />
                Meeting Recording
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Audio Settings Panel - Show only when not recording and no existing recording */}
              {(!recording || recording.status !== 'completed' || recordingFileMissing) && !isRecording && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-muted-foreground">Audio Configuration</span>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => setShowAudioSettings(!showAudioSettings)}
                      data-testid="audio-settings-toggle"
                    >
                      {showAudioSettings ? 'Hide Settings' : 'Show Settings'}
                    </Button>
                  </div>
                  
                  {showAudioSettings && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg border space-y-4" data-testid="audio-settings-panel">
                      {/* Device Selection */}
                      <div className="space-y-2">
                        <Label className="text-sm">Microphone</Label>
                        <Select value={selectedDevice} onValueChange={setSelectedDevice}>
                          <SelectTrigger data-testid="device-select">
                            <SelectValue placeholder="Select microphone" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">
                              <div className="flex items-center gap-2">
                                <Mic className="h-4 w-4" />
                                System Default
                              </div>
                            </SelectItem>
                            {audioDevices.map(device => (
                              <SelectItem key={device.deviceId} value={device.deviceId}>
                                {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">{audioDevices.length} device(s) detected</p>
                      </div>
                      
                      {/* Quality Preset Selection */}
                      <div className="space-y-2">
                        <Label className="text-sm">Audio Quality</Label>
                        <div className="grid grid-cols-3 gap-2">
                          {Object.entries(AUDIO_PRESETS).map(([key, preset]) => (
                            <button
                              key={key}
                              onClick={() => setAudioPreset(key)}
                              className={`p-3 rounded-lg border-2 text-left transition-all ${
                                audioPreset === key 
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                              }`}
                              data-testid={`preset-${key}`}
                            >
                              <div className="text-sm font-medium">{preset.name}</div>
                              <div className="text-xs text-muted-foreground">{preset.description}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                      
                      {/* Test Microphone */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const stream = await navigator.mediaDevices.getUserMedia({
                              audio: selectedDevice !== 'default' ? { deviceId: { exact: selectedDevice } } : true
                            });
                            toast.success('Microphone working!');
                            stream.getTracks().forEach(track => track.stop());
                          } catch (err) {
                            toast.error('Could not access microphone');
                          }
                        }}
                        data-testid="test-mic-btn"
                      >
                        Test Microphone
                      </Button>
                    </div>
                  )}
                  
                  {/* Current Settings Badge */}
                  {!showAudioSettings && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Badge variant="outline" className="font-normal">
                        {AUDIO_PRESETS[audioPreset].name}
                      </Badge>
                      <span>•</span>
                      <span>{audioDevices.find(d => d.deviceId === selectedDevice)?.label || 'Default Mic'}</span>
                    </div>
                  )}
                </div>
              )}
              
              {/* Recording Controls */}
              {(!recording || recording.status !== 'completed' || recordingFileMissing) && (
                <div className="flex flex-col items-center gap-6 p-8 bg-muted rounded-xl">
                  {isRecording ? (
                    <div className="text-center w-full max-w-md">
                      <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-4 mx-auto ${isPaused ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-red-100 dark:bg-red-900/30 animate-pulse'}`}>
                        <Mic className={`h-12 w-12 ${isPaused ? 'text-amber-600' : 'text-red-600'}`} />
                      </div>
                      <p className={`text-2xl font-mono font-bold mb-4 ${isPaused ? 'text-amber-600' : 'text-red-600'}`}>
                        {formatTime(recordingTime)} {isPaused && '(PAUSED)'}
                      </p>
                      
                      {/* Audio Level */}
                      <div className="mb-4">
                        <div className="flex justify-center gap-1 h-8">
                          {[...Array(20)].map((_, i) => (
                            <div key={i} className={`w-2 rounded-full transition-all ${i < audioLevel/5 ? (audioLevel > 60 ? 'bg-green-500' : audioLevel > 30 ? 'bg-yellow-500' : 'bg-red-500') : 'bg-muted-foreground/20'}`} style={{ height: `${Math.max(8, (i+1)*1.6)}px` }} />
                          ))}
                        </div>
                      </div>
                      
                      {/* Live Transcript */}
                      {(liveTranscript || interimTranscript) && (
                        <div className="mb-4 p-4 bg-background rounded-lg border max-h-32 overflow-y-auto text-left">
                          <p className="text-sm">{liveTranscript}<span className="text-blue-500 italic">{interimTranscript}</span></p>
                        </div>
                      )}
                      
                      <div className="flex justify-center gap-4">
                        {isPaused ? (
                          <Button onClick={resumeRecording} className="bg-green-600 hover:bg-green-700">Resume</Button>
                        ) : (
                          <Button variant="outline" onClick={pauseRecording}>Pause</Button>
                        )}
                        <Button variant="destructive" onClick={stopRecording}>
                          <StopCircle className="h-4 w-4 mr-2" />Stop & Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center space-y-4">
                      <Button size="lg" className="bg-red-600 hover:bg-red-700" onClick={startRecording}>
                        <PlayCircle className="h-5 w-5 mr-2" />Start Recording
                      </Button>
                      <div className="flex gap-4">
                        <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                          <Upload className="h-4 w-4 mr-2" />Upload Audio
                        </Button>
                        <Button variant="ghost" onClick={() => setExternalUrlDialog(true)}>
                          <Globe className="h-4 w-4 mr-2" />External Link
                        </Button>
                      </div>
                      <input ref={fileInputRef} type="file" accept="audio/*,video/*" className="hidden" onChange={handleFileUpload} />
                    </div>
                  )}
                </div>
              )}

              {/* Upload Progress */}
              {uploadProgress > 0 && uploadProgress < 100 && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm"><span>Uploading...</span><span>{uploadProgress}%</span></div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}

              {/* Recording Player */}
              {recording?.status === 'completed' && !recordingFileMissing && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 dark:text-green-400 mb-4">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Recording Uploaded</span>
                    <span className="text-sm text-muted-foreground ml-auto">{recording.file_name}</span>
                  </div>
                  <audio controls className="w-full" src={`${API}/api/meeting-intelligence/meetings/${meetingId}/recording/stream?token=${token}`} onError={() => setRecordingFileMissing(true)} />
                </div>
              )}

              {/* Transcription Button */}
              {recording?.status === 'completed' && !recordingFileMissing && (
                <div className="flex gap-4">
                  <Button onClick={runTranscription} disabled={processing.transcription}>
                    {processing.transcription ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Transcribing ({transcriptionProgress}%)...</> : <><Wand2 className="h-4 w-4 mr-2" />Generate Transcript</>}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transcript Tab - Using modular component */}
        <TabsContent value="transcript">
          <TranscriptTab
            meeting={meeting}
            token={token}
            transcript={transcript}
            onTranscriptUpdate={(updatedTranscript) => {
              setTranscript(updatedTranscript);
              if (updatedTranscript?.text) {
                setTranscriptText(updatedTranscript.text);
              }
            }}
          />
        </TabsContent>

        {/* Analysis Tab - Using modular component */}
        <TabsContent value="analysis">
          <AnalysisTab
            meeting={{...meeting, transcript}}
            token={token}
            analysis={analysis}
            onAnalysisUpdate={(updatedAnalysis) => {
              setAnalysis(updatedAnalysis);
              fetchMeetingData();
            }}
          />
        </TabsContent>

        {/* Minutes Tab */}
        <TabsContent value="minutes">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5" />Minutes of Meeting</CardTitle>
                  {minutes && <Badge variant={minutes.approval_status === 'approved' ? 'success' : 'warning'} className="mt-2">{minutes.approval_status}</Badge>}
                </div>
                <div className="flex gap-2">
                  {!minutes && transcript && (
                    <Button onClick={generateMoM} disabled={processing.mom}>
                      {processing.mom ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Wand2 className="h-4 w-4 mr-2" />Generate MoM</>}
                    </Button>
                  )}
                  {minutes && !editingMinutes && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setEditingMinutes(true)}><Edit className="h-4 w-4 mr-2" />Edit</Button>
                      {minutes.approval_status !== 'approved' && <Button variant="outline" size="sm" onClick={() => setApprovalDialog(true)}>Approve</Button>}
                      {minutes.approval_status === 'approved' && (
                        <>
                          <Button size="sm" onClick={() => setSendDialog(true)}><Send className="h-4 w-4 mr-2" />Send Email</Button>
                          {meeting?.whatsapp_chat_id && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={sendWhatsAppNotification}
                              disabled={sendingWhatsApp}
                              className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                            >
                              {sendingWhatsApp ? (
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              ) : (
                                <MessageCircle className="h-4 w-4 mr-2" />
                              )}
                              WhatsApp
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {editingMinutes && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setEditingMinutes(false)}>Cancel</Button>
                      <Button size="sm" onClick={saveMinutes}><Save className="h-4 w-4 mr-2" />Save</Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {minutes ? (
                editingMinutes ? (
                  <Textarea value={minutesText} onChange={(e) => setMinutesText(e.target.value)} rows={20} />
                ) : (
                  <div className="prose dark:prose-invert max-w-none">
                    <pre className="whitespace-pre-wrap text-sm bg-muted p-4 rounded-lg">{minutes.content}</pre>
                  </div>
                )
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No minutes yet</p>
                  <p className="text-sm">Generate transcript and analysis first</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks">
          <TasksTab meetingId={meetingId} tasks={tasks} token={token} onTasksUpdate={fetchMeetingData} />
        </TabsContent>

        {/* Decisions Tab */}
        <TabsContent value="decisions">
          <DecisionsTab meetingId={meetingId} decisions={decisions} token={token} onDecisionsUpdate={fetchMeetingData} />
        </TabsContent>
      </Tabs>

      {/* ============ Dialogs ============ */}
      
      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onOpenChange={setApprovalDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Approve Minutes</DialogTitle></DialogHeader>
          <p className="text-muted-foreground">Are you sure you want to approve these minutes?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApprovalDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={() => approveMoM('reject')}><ThumbsDown className="h-4 w-4 mr-2" />Reject</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={() => approveMoM('approve')}><ThumbsUp className="h-4 w-4 mr-2" />Approve</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={sendDialog} onOpenChange={setSendDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Minutes</DialogTitle></DialogHeader>
          <div className="flex gap-2 p-1 bg-muted rounded-lg mb-4">
            <button onClick={() => setSendMethod('email')} className={`flex-1 py-2 px-4 rounded-md ${sendMethod === 'email' ? 'bg-background shadow' : ''}`}><Mail className="h-4 w-4 inline mr-2" />Email</button>
            <button onClick={() => setSendMethod('whatsapp')} className={`flex-1 py-2 px-4 rounded-md ${sendMethod === 'whatsapp' ? 'bg-background shadow' : ''}`}><Phone className="h-4 w-4 inline mr-2" />WhatsApp</button>
          </div>
          {sendMethod === 'email' ? (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {meeting.attendees?.filter(a => a.email).map((a, i) => (
                <div key={i} onClick={() => toggleRecipient(a.email)} className={`p-3 rounded-lg cursor-pointer ${selectedRecipients.includes(a.email) || selectedRecipients.length === 0 ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200' : 'bg-muted'}`}>
                  <p className="font-medium text-sm">{a.name}</p>
                  <p className="text-xs text-muted-foreground">{a.email}</p>
                </div>
              ))}
            </div>
          ) : (
            <Textarea value={whatsappNumbers} onChange={(e) => setWhatsappNumbers(e.target.value)} placeholder="+971501234567, +971509876543" rows={3} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialog(false)}>Cancel</Button>
            <Button onClick={sendMoM} disabled={processing.sending}>{processing.sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Send</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* External URL Dialog */}
      <Dialog open={externalUrlDialog} onOpenChange={setExternalUrlDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>External Recording URL</DialogTitle></DialogHeader>
          <Input value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)} placeholder="https://drive.google.com/..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setExternalUrlDialog(false)}>Cancel</Button>
            <Button onClick={saveExternalUrl}>Save URL</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={reminderDialog} onOpenChange={setReminderDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Reminder</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reminder Type</Label>
              <Select value={reminderType} onValueChange={setReminderType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upcoming">Upcoming Meeting</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="mom_review">MoM Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Custom Message (optional)</Label>
              <Textarea value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderDialog(false)}>Cancel</Button>
            <Button onClick={sendReminder} disabled={sendingReminder}>{sendingReminder ? 'Sending...' : 'Send Reminder'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post Recording Form Dialog */}
      <Dialog open={showPostRecordingForm} onOpenChange={setShowPostRecordingForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Complete Meeting Details</DialogTitle><DialogDescription>Fill in the meeting details after recording</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Meeting Title</Label>
              <Input value={postRecordingData.title} onChange={(e) => setPostRecordingData(p => ({ ...p, title: e.target.value }))} placeholder="Meeting title" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={postRecordingData.description} onChange={(e) => setPostRecordingData(p => ({ ...p, description: e.target.value }))} rows={3} />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={postRecordingData.location} onChange={(e) => setPostRecordingData(p => ({ ...p, location: e.target.value }))} placeholder="Meeting room or virtual" />
            </div>
            <div>
              <Label>Attendees</Label>
              <Select onValueChange={(userId) => {
                const user = orgUsers.find(u => u.id === userId);
                if (user && !postRecordingData.attendees.find(a => a.user_id === userId)) {
                  setPostRecordingData(p => ({ ...p, attendees: [...p.attendees, { user_id: user.id, name: user.full_name, email: user.email, is_internal: true }] }));
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Select attendees" /></SelectTrigger>
                <SelectContent>{orgUsers.map(u => <SelectItem key={u.id} value={u.id}>{u.full_name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex flex-wrap gap-2 mt-2">
                {postRecordingData.attendees.map((a, i) => (
                  <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => setPostRecordingData(p => ({ ...p, attendees: p.attendees.filter((_, idx) => idx !== i) }))}>
                    {a.name} {!a.is_internal && '(External)'} ×
                  </Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <Input value={externalAttendeeInput.name} onChange={(e) => setExternalAttendeeInput(p => ({ ...p, name: e.target.value }))} placeholder="External name" className="flex-1" />
              <Input value={externalAttendeeInput.email} onChange={(e) => setExternalAttendeeInput(p => ({ ...p, email: e.target.value }))} placeholder="Email" className="flex-1" />
              <Button variant="outline" size="sm" onClick={() => {
                if (externalAttendeeInput.name && externalAttendeeInput.email) {
                  setPostRecordingData(p => ({ ...p, attendees: [...p.attendees, { ...externalAttendeeInput, is_internal: false }] }));
                  setExternalAttendeeInput({ name: '', email: '' });
                }
              }}>Add</Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPostRecordingForm(false)}>Skip</Button>
            <Button onClick={handlePostRecordingSubmit} disabled={savingMeetingDetails}>{savingMeetingDetails ? 'Saving...' : 'Save & Process'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MeetingDetail;
